import { Graph, GraphColumn } from "./generic-graph";

/**
 * Atomic Graph Part - Holds data for all types of vertical graphs
 */
export interface VerticalGraph {
  columnType: String; //for example M for millions, etc will show as 62M
  data: VerticalGraphItem[];
}
export interface AreaGraph {
  columnType: String; //for example M for millions, etc will show as 62M
  data: AreaGraphItem[];
}
export interface FixedCostGraph {
  data: FixedCostItem[];
  rowNames: String[];  //names of row columns for example ACT LM % GAP
}

export interface VerticalGraphItem extends GraphColumn {
  partial?: VerticalGraphColumn;
  current: VerticalGraphColumn;
  forecast?: VerticalGraphColumn; // for rfc graph
  forecastPercent?: number; // for rfc graph, should be null if not present or other graphs
  isUnderForecast?: boolean; // for rfc graph, controls color true for red, false for green
  compare?: VerticalGraphColumn; 
  isPreliminary?: boolean;
  isDefault?: boolean;
  isFuture?: boolean;
  colName: String;
  colGroupName: String;
  cif?: number; //relevant only in potash avg
  fob?: number; //relevant only in potash avg
}

export interface AreaVerticalGraphItem extends GraphColumn {
  isPreliminary?: boolean;
  isDefault?: boolean;
  isFuture?: boolean;
  colName: String;
  colGroupName: String;
}

export interface FixedCostVerticalGraphItem {
  isDefault?: boolean;
  colName: String;
}
export interface AreaGraphItem {
  data: number[];
  colName: String;
}
export interface FixedCostItem {
  data: String[][]; // array of arrays of string values for example [['100','-100'],['110','100%']]
  colName: String; // row title for example R&D
  iconName: FixedCostItemIcons;
}

export enum FixedCostItemIcons {
  'iclIcon', //icl logo 
  'laborIcon', //pen and ruler icon
  'mainIcon', //house icon
  'officeIcon', //chair icon
  'settingsIcon', //cog icon
  'arrowUpIcon',//line arrow up icon
  'briefcaseIcon',//briefcase icon
  'rndIcon' //flask icon
}

export interface VerticalGraphColumn {
  value: number;
  percent?: number;
}


/**
 * Atomic Graph Part - Holds data for summary batteries in vertical graphs etc.
 */
export interface SummaryBattery {
  title: String;
  colNames: String[]; //in order - for example ['ACT', 'LM']
  columns: SummaryBatteryItem[];
}

export interface SummaryBatteryItem {
  title: String;
  value: number;
  columns: any[]; //in order according to outer array
  negative?: boolean;
  size?: number;
  abs?: number;
}


/**
 * graph view (for example sales, oi, ebitda)
 */
export interface VerticalGraphView extends Graph {
  data: VerticalGraphViewData[]; //in main graph - only 1 element in array.
  isMainGraph: boolean;
}
export interface AreaGraphView extends Graph {
  data: AreaGraphViewData[]; //in main graph - only 1 element in array.
  isMainGraph: boolean;
  colData: AreaVerticalGraphItem[];
}

export interface FixedCostGraphView extends Graph {
  data: FixedGraphViewData[]; 
  isMainGraph: boolean;
  colData: FixedCostVerticalGraphItem[]; // for the slider notches values and amount, can be 1-12 or Q1 Q2 Q3 Q4
}
export interface SalesWaterfallGraphView extends Graph {
  data: SalesWaterfallViewData[]; 
  isMainGraph: boolean;
  colData: FixedCostVerticalGraphItem[]; // for the slider notches values and amount, can be 1-12 or Q1 Q2 Q3 Q4 same as fixed cost
}
export interface SalesVarGraphView extends Graph {
  data: SalesVarViewData[]; 
  isMainGraph: boolean;
  colData: FixedCostVerticalGraphItem[]; // for the slider notches values and amount, can be 1-12 or Q1 Q2 Q3 Q4 same as fixed cost
}
export interface VerticalGraphViewData {
  title: String; //for example "Total, ADS etc"
  subTitle?: String; //for example "Total, ADS etc" used for secondary nav in potash avg price
  graph: VerticalGraph;
  battery: SummaryBattery[]; // same order as graph columns
  lineGraph?: AreaGraph; // used for potash average, if not null will render a line chart above normal bar chart
}

export interface AreaGraphViewData {
  title: String; //for example "Total, ADS etc"
  graph: AreaGraph;
  battery: SummaryBattery[]; // same order as graph columns
}
export interface FixedCostFxSelectorData{
  title: String; //button title Adj FX, Off FX
  graph: FixedCostGraph;
  battery: SummaryBattery[]; // same order as graph columns
}
export interface FixedGraphViewData {
  title: String; //for example "Total, ADS etc" (SBU)
  data: FixedCostFxSelectorData[] // code expects 2 objects with Adj Fx and Off FX
}
export interface SalesWaterfallViewData{
  title: String; //for example "Total, ADS etc" (SBU)
  graph: SaleswaterfallGraph 
  table: SaleswaterfallTable[] 
}
export interface SalesVarViewData{
  title: String; //for example "Total, ADS etc" (SBU)
  table: SalesVarTable[] 
}
export interface SaleswaterfallGraph{
  columnType: String; //for example M for millions, etc will show as 62M
  data: Number[][]; // first item is the starting point, then every number after that is just the changes from previous number, last item will always be the end result, for example [10,2,-2,-4,6] (6 is the result of all previous operations)
  //double array because of months 
  rows: String[];  //names of row columns for example ACT LM % GAP
}
export interface SaleswaterfallTable{
  title: String; // name of month for example 1 or 12 or Q2
  colNames: String[];  //names of row columns for example ACT LM % GAP
  columns: String[][]; /// the values of the table, array of rows, each row is an array from values going left to rigth
}
export interface SalesVarTable{
  title: String; // name of month for example 1 or 12 or Q2
  sales: SalesVarTableUnit[]; // one object per row for the relevant view
  qtyAsc: SalesVarTableUnit[]; // one object per row for the relevant view
  qtyDesc: SalesVarTableUnit[]; // one object per row for the relevant view
  priceAsc: SalesVarTableUnit[]; // one object per row for the relevant view
  priceDesc: SalesVarTableUnit[]; // one object per row for the relevant view
}
export interface SalesVarTableUnit{
name:String;
sales:String;
qty:String;
price:String;
}
/**
 * Potash Avg price
 */

export interface PotashAveragePriceView extends Graph {
  data: VerticalGraph;
}

/**
 * EPS graph view
 */

export interface EPSGraphView extends Graph {
  data: VerticalGraph;
  footer: EPSDataItem[]; //notice - array for each column in vertical graph, array item for ni columns

}

export interface EPSDataItem {
  title:string;
  dilutedShares: number; //fixed 2 point decimal
  adjustedNI: number; //fixed 2 point decimal
  dilutedSharesCompare: number; //fixed 2 point decimal
  adjustedNICompare: number; //fixed 2 point decimal
}

/**
 * NI Graph view
 */

export interface NIGraphView extends Graph {
  data: VerticalGraph;
}


export enum VerticalGraphType {
  SIMPLE_BAR,
  COMPARE_BAR,
}






